import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import qs from 'qs'

import { SessionProvider } from '@shared/plugins/session-provider'
import { useGeneralStore } from '@shared/store/general'
import { errorInterceptor } from '@shared/utils/helpers/error-guards/error-interceptor'
import { sanitizeRequest } from '@shared/utils/helpers/sanitize-request'

import { version } from '@/package.json'
import { JsonValue, Message } from '@bufbuild/protobuf'

// https://dirask.com/questions/JavaScript-Uncaught-TypeError-Do-not-know-how-to-serialize-a-BigInt-13lmaD
// BigInt is not supported by JSON.stringify
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
BigInt.prototype.toJSON = function () {
  return this.toString()
}

export class ApiTransport {
  namespace?: string
  private readonly http: AxiosInstance

  constructor(baseUrl: string, namespace?: string) {
    this.namespace = namespace

    const url = namespace ? [baseUrl, namespace].join('/') : baseUrl

    this.http = axios.create({
      baseURL: new URL(url, SessionProvider.baseUrl()).href,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        version,
      },
    })
    this.http.interceptors.request.use((config) => {
      const { accessToken, deviceUuid } =
        SessionProvider.getStoredSession() ?? {}
      if (accessToken && deviceUuid) {
        config.headers!.authorization = `Bearer ${accessToken}`
        config.headers!['X-Fingerprint'] = deviceUuid
      }

      return config
    })

    this.http.interceptors.response.use((response) => {
      const generalStore = useGeneralStore()
      generalStore.updateLastRequestTime()
      return response
    }, errorInterceptor)
  }

  async getBinaryData<T extends Message<T>>(
    path: string,
    requestProto: T
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any | undefined> {
    const queryString = requestProto
      ? qs.stringify(requestProto.toJson(), {
          arrayFormat: 'brackets',
        })
      : ''
    const response = await this.http.get<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path, queryString].join('?'), { responseType: 'arraybuffer' })
    return response?.data
  }

  async get<T extends Message<T>>(
    path: string,
    requestProto: T
  ): Promise<undefined>
  async get<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    responseProtoType: new () => R,
    signal?: AbortSignal
  ): Promise<R>
  async get<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    responseProtoType?: new () => R,
    signal?: AbortSignal
  ): Promise<R | undefined> {
    const queryString = requestProto
      ? qs.stringify(requestProto.toJson(), {
          arrayFormat: 'brackets',
        })
      : ''

    const response = await this.http.get<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path, queryString].join('?'), { signal: signal })

    if (!responseProtoType) {
      return
    }

    return this.instantiate<R>(responseProtoType, response?.data)
  }

  async getWithoutRequest<T extends Message<T>>(
    path: string,
    responseProtoType: new () => T
  ): Promise<T>
  async getWithoutRequest<T extends Message<T>>(path: string): Promise<null>
  async getWithoutRequest<T extends Message<T>>(
    path: string,
    responseProtoType?: new () => T
  ): Promise<T | null> {
    const response = await this.http.get<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path].join('?'))

    if (!responseProtoType) {
      return null
    }
    return this.instantiate<T>(responseProtoType, response?.data)
  }

  async post<T extends Message<T>>(
    path: string,
    requestProto: T,
    requestProtoType: new () => T
  ): Promise<JsonValue>
  async post<T extends Message<T>>(
    path: string,
    requestProto: T,
    requestProtoType: new () => T,
    responseProtoType?: new () => T,
    config?: AxiosRequestConfig
  ): Promise<JsonValue>
  async post<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    requestProtoType: new () => T,
    responseProtoType: new () => R,
    config?: AxiosRequestConfig
  ): Promise<R>
  async post<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    requestProtoType?: new (arg: T) => T,
    responseProtoType?: new () => R,
    config?: AxiosRequestConfig
  ): Promise<R | JsonValue> {
    const params = requestProtoType
      ? new requestProtoType(sanitizeRequest<T>(requestProto))
      : sanitizeRequest<T>(requestProto)

    const { data: response } = await this.http.post<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path].join('?'), params, config || {})

    if (!responseProtoType) {
      return response as JsonValue
    }

    return this.instantiate<R>(responseProtoType, response)
  }

  async postWithoutRequest<T extends Message<T>>(
    path: string,
    responseProtoType?: new () => T
  ): Promise<T>
  async postWithoutRequest<T extends Message<T>>(path: string): Promise<null>
  async postWithoutRequest<T extends Message<T>>(
    path: string,
    responseProtoType?: new () => T,
    config?: AxiosRequestConfig
  ): Promise<T | null | undefined> {
    const { data: response } = await this.http.post<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path].join('?'), {}, config || {})

    if (!responseProtoType) {
      return
    }

    return this.instantiate<T>(responseProtoType, response)
  }

  async put<T extends Message<T>>(
    path: string,
    requestProto: T,
    requestProtoType: new () => T
  ): Promise<undefined>
  async put<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    requestProtoType: new () => T,
    responseProtoType: new () => R
  ): Promise<R>
  async put<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    requestProtoType: new () => T,
    responseProtoType: new () => R,
    config: AxiosRequestConfig
  ): Promise<R>
  async put<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    requestProtoType?: new (arg: T) => T,
    responseProtoType?: new () => R,
    config?: AxiosRequestConfig
  ): Promise<R | undefined> {
    const params = requestProtoType
      ? new requestProtoType(sanitizeRequest<T>(requestProto))
      : sanitizeRequest<T>(requestProto)

    const response = await this.http.put<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path].join('?'), params, config || {})

    if (!responseProtoType) {
      return
    }

    return this.instantiate<R>(responseProtoType, response.data)
  }

  async putWithoutRequest<T extends Message<T>>(
    path: string,
    responseProtoType?: new () => T
  ): Promise<T>
  async putWithoutRequest<T extends Message<T>>(path: string): Promise<null>
  async putWithoutRequest<T extends Message<T>>(
    path: string,
    responseProtoType?: new () => T
  ): Promise<T | null | undefined> {
    const response = await this.http.put<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path].join('?'), {})

    if (!responseProtoType) {
      return
    }

    return this.instantiate<T>(responseProtoType, response.data)
  }

  async delete<T extends Message<T>>(
    path: string,
    requestProto: T
  ): Promise<undefined>
  async delete<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    requestProtoType: new () => T,
    responseProtoType: new () => R
  ): Promise<R>
  async delete<T extends Message<T>, R extends Message<R>>(
    path: string,
    requestProto: T,
    requestProtoType?: new (arg: T) => T,
    responseProtoType?: new () => R
  ): Promise<R | undefined> {
    const data = requestProtoType
      ? new requestProtoType(sanitizeRequest<T>(requestProto))
      : sanitizeRequest<T>(requestProto)

    const { data: response } = await this.http.delete<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path].join('?'), { data })

    if (!responseProtoType) {
      return
    }

    return this.instantiate<R>(responseProtoType, response)
  }

  async deleteWithoutRequest<T extends Message<T>>(
    path: string,
    responseProtoType?: new () => T
  ): Promise<T>
  async deleteWithoutRequest<T extends Message<T>>(path: string): Promise<null>
  async deleteWithoutRequest<T extends Message<T>>(
    path: string,
    responseProtoType?: new () => T
  ): Promise<T | null | undefined> {
    const { data: response } = await this.http.delete<
      JsonValue,
      AxiosResponse<JsonValue, JsonValue>,
      JsonValue
    >([path].join('?'))

    if (!responseProtoType) {
      return
    }

    return this.instantiate<T>(responseProtoType, response)
  }

  private instantiate<T extends Message<T>>(
    protoType: new () => T,
    json: JsonValue
  ): T {
    try {
      return new protoType().fromJson(json)
    } catch (error) {
      const message = (error as Error).message

      // eslint-disable-next-line
      console.error(
        `[Vue 3 application] response of type ${protoType.name} parsing error (${message}):`,
        json
      )
      return new protoType().fromJson(json, { ignoreUnknownFields: true })
    }
  }
}
